// domain/constants/degree.js

/**
 * The rungs of the education ladder, in order.
 *
 * A LADDER RATHER THAN A LIST, because every rule that reads a degree asks a comparative question:
 * does this person hold a master's or better, is this at least a bachelor's. So the rungs carry an
 * explicit rank and comparison goes through it, rather than through the order somebody happened to
 * declare them in.
 *
 * WHAT IS RECORDED IS THE RUNG, NOT THE TITLE ON THE DIPLOMA. BA and BS are the same rung and no rule
 * distinguishes them, so storing "bachelor" and the field of study separately answers every question
 * a payer asks while leaving nothing to interpret. The awarded title is on the proof document, which
 * is what an auditor reads.
 *
 * THERE IS NO RUNG FOR "BELOW A BACHELOR'S". That is the absence of a qualifying degree rather than a
 * degree somebody holds, so it is what resolving finds when nothing on this ladder applies, never a
 * record anyone authors. `associate` exists because it is a real award people hold and file proof
 * for; it simply sits below the rung most rules ask about.
 */
export const DEGREE_LEVELS = {
  ASSOCIATE: 'associate',
  BACHELOR: 'bachelor',
  MASTER: 'master',
  DOCTORATE: 'doctorate',
};

/** Rank for comparison. Higher is further up the ladder; the numbers are ordering only. */
const DEGREE_LEVEL_RANK = {
  [DEGREE_LEVELS.ASSOCIATE]: 1,
  [DEGREE_LEVELS.BACHELOR]: 2,
  [DEGREE_LEVELS.MASTER]: 3,
  [DEGREE_LEVELS.DOCTORATE]: 4,
};

export const isDegreeLevel = (value) => Object.hasOwn(DEGREE_LEVEL_RANK, value);

/**
 * How far up the ladder a level sits, or 0 for anything that is not a level.
 *
 * Zero rather than null so that comparing an unrecognized value never ranks it ABOVE a real degree,
 * which is the direction that would let an unreadable record bill as a doctorate.
 */
export const degreeLevelRank = (level) => DEGREE_LEVEL_RANK[level] ?? 0;

/** The higher of two levels, or null when neither is one. */
export function highestDegreeLevel(left, right) {
  const best = degreeLevelRank(left) >= degreeLevelRank(right) ? left : right;

  return isDegreeLevel(best) ? best : null;
} // highestDegreeLevel()
